import { TranslationService } from './../../../services/translation/translation.service';
import { PagedResult } from '../../../models/pagedResult.model';
import { Router } from '@angular/router';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, NgZone, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { OfferService } from '../../../services/offer/offer.service';
import { Offer } from '../../../models/offer.model';
import { MatDialog } from '@angular/material/dialog';

import { SnackbarService } from '../../../services/shared/snackbar/snackbar.service';
import { DialogService } from '../../../services/shared/dialog/dialog.service';
import { AddOfferModalComponent } from '../../../shared/components/add-offer-modal/add-offer-modal.component';
import { UtilsService } from '../../../services/shared/utils/utils.service';
import { merge, Subject, takeUntil } from 'rxjs';
import { LoadingService } from '../../../services/loading/loading.service';

@Component({
  selector: 'app-offers',
  templateUrl: './offers.component.html',
  styleUrl: './offers.component.css'
})
export class OffersComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns: string[] = ['name', 'code', 'pages', 'isRequired', 'hidden', 'expirationDelay', 'actions'];
  dataSource = new MatTableDataSource<Offer>();
  totalItems = 0;

  search: string = "";

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private offerService: OfferService,
    private snackBar: SnackbarService,
    public dialog: MatDialog,
    public dialogService: DialogService,
    public translationService: TranslationService,
    public utils: UtilsService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private loadingService: LoadingService) { }

  ngOnInit() {
    this.getOffers();
  }

  ngAfterViewInit(): void {
    this.sort.sortChange
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
      this.paginator.pageIndex = 0;
    });

    merge(this.paginator.page, this.sort.sortChange)
    .pipe(takeUntil(this.destroy$))
    .subscribe(() => {
      this.getOffers();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getOffers() {
    const pageIndex = this.paginator?.pageIndex ?? 0;
    const pageSize = this.paginator?.pageSize ?? 10;
    const sortField = this.sort?.active ?? 'id';
    const sortOrder = this.sort?.direction === 'asc' ? 'asc' : 'desc';
    this.loadingService.show();

    this.offerService.getOffers(pageIndex + 1, pageSize, sortField, sortOrder, this.search)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result: PagedResult<Offer>) => {
        this.totalItems = result.totalCount;

        this.ngZone.run(() => {
          this.dataSource.data = result.items;
        });

        this.loadingService.hide();
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.loadingService.hide();
        this.snackBar.showError(this.translationService.translate('Error fetching offers'));
      }
    });
  }

  deleteOffer(e: MouseEvent, offer: Offer): void {
    e.preventDefault();
    e.stopPropagation();
    this.dialogService
    .confirm(this.translationService.translate('Confirm Delete'), this.translationService.translate('Are you sure you want to delete this offer?'))
    .then((confirmed) => {
      if (confirmed) {
        if (offer.id && (!offer.pages || offer.pages.length <= 0)) {
          this.offerService.deleteOffer(offer.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.snackBar.showSuccess(this.translationService.translate('Offer deleted successfully'));
              this.getOffers();
            },
            error: (error) => {
              this.snackBar.showError(this.translationService.translate('Error fetching offer'));
            }
          });
        } else {
          this.snackBar.showError(this.translationService.translate('ID is null'));
        }
      }
    });
  }

  navigateToOffer(offer: Offer): void {
    this.router.navigate(['/admin/offers/', offer.id]);
  }

  addNewOffer(): void {
    this.offerService.getAllOffersCodes()
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (result: string[]) => {
        const dialogRef = this.dialog.open(AddOfferModalComponent, {
          width: '500px',
          data: { codes: result }
        });

        dialogRef.afterClosed().subscribe(offer => {
          if (offer !== undefined) {
            this.offerService.createOffer(offer)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.snackBar.showSuccess(this.translationService.translate('Offer created successfully'));
                this.getOffers();
              },
              error: (error) => {
                this.snackBar.showError(this.translationService.translate('Error creating offer'));
              }
            });
          }
        });
      },
      error: (error) => {
        this.snackBar.showError(this.translationService.translate('Error fetching offers codes'));
      }
    });
  }

  applyFilter(event: KeyboardEvent): void {
    this.search = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.paginator.pageIndex = 0;
    this.getOffers();
  }
}
